import type { ReactElement } from 'react';
import styled from '@emotion/styled';
import { t } from 'i18next';
import { observer } from 'mobx-react';
import { useStore } from '@stores/RootStore';
import type { LogicalOperator } from '@repositories/workflowRepository/workflow.types';
import type { WorkflowBaseTriggerFieldDTOModel } from '@repositories/workflowBaseTriggerRepository/models/WorkflowBaseTriggerFieldDTOModel';
import { ConditionSetting } from './ConditionSetting';

export const DirectApiConfig = observer(() => {
  const { workflowDetailStore } = useStore();

  const baseTrigger = workflowDetailStore.triggerNode?.data.baseTriggerModel;
  const triggerModel = workflowDetailStore.triggerNode?.data.triggerModel;

  const handleChangeLogicalOperator = (operator: LogicalOperator, index: number) => {
    if (!triggerModel) return;
    if (!triggerModel.conditions) triggerModel.conditions = [];

    triggerModel.conditions.splice(index, 1, { ...triggerModel.conditions[index], logicalOperator: operator });
  };

  const handleChangeField = (field: WorkflowBaseTriggerFieldDTOModel, index: number) => {
    if (!triggerModel) return;
    if (!triggerModel.conditions) triggerModel.conditions = [];

    triggerModel.conditions.splice(index, 1, {
      ...triggerModel.conditions[index],
      fieldName: field.name,
      value: undefined,
    });
  };

  const handleChangeValue = (value: string | undefined, index: number) => {
    if (!triggerModel) return;
    if (!triggerModel.conditions) triggerModel.conditions = [];

    triggerModel.conditions.splice(index, 1, { ...triggerModel.conditions[index], value });
  };

  const handleClickAddBtn = (index: number) => {
    if (!triggerModel) return;
    if (!triggerModel.conditions) triggerModel.conditions = [];

    triggerModel.conditions.splice(index + 1, 0, { fieldName: '' });
  };

  const handleClickRemoveBtn = (index: number) => {
    if (!triggerModel) return;
    if (!triggerModel.conditions) triggerModel.conditions = [];

    triggerModel.conditions.splice(index, 1);

    // NOTE. 첫번째 조건을 삭제하여 첫번째 조건이되는 다음 조건은 오퍼레이터는 리셋되어야함.
    if (index === 0 && triggerModel.conditions.length > 0) {
      triggerModel.conditions[index].logicalOperator = undefined;
    }
  };

  const conditions = triggerModel?.conditions ?? [];

  let conditionSettings: ReactElement[] = [];
  if (baseTrigger) {
    conditionSettings = conditions.map((condition, idx) => {
      const key = `${idx}`;
      return (
        <ConditionSetting
          key={key}
          title={`${t('Setting')} ${idx + 1}`}
          condition={condition}
          conditionsLength={conditions.length}
          baseTrigger={baseTrigger}
          isEditable={workflowDetailStore.checkEditable()}
          isLogicalOperatorDisabled={idx === 0}
          onChangeLogicalOperator={logicalOp => handleChangeLogicalOperator(logicalOp, idx)}
          onChangeField={field => handleChangeField(field, idx)}
          onChangeValue={value => handleChangeValue(value, idx)}
          onClickAddBtn={() => handleClickAddBtn(idx)}
          onClickRemoveBtn={() => handleClickRemoveBtn(idx)}
        />
      );
    });
  }

  return <Container>{conditionSettings.length > 0 && conditionSettings}</Container>;
});

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
  width: 100%;
`;
